import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Error as ErrorBlock } from "../../../common/Error";
import { HousingItem } from "../../../../pages/Housings/HousingItem";
import type {
  VariantType,
  BackendSuccessType,
  BackendErrorType,
  ViewCollectionPropsType,
} from "./types";
import { parseCodeFromUrl, formatRub, installmentLabel } from "./utils";
import { baseHost } from "../../../../constants/common";
import { pluralizeRu } from "../../../../utils/pluralizeRu";
import "./styles.css";

const toHousing = (variant: VariantType) => variant.housing || {
  id: variant.housingId || variant.id,
  housingName: variant.housingName || "Жилой комплекс",
  address: variant.address || "",
  builder: variant.builder || "",
  images: variant.images?.length ? variant.images : variant.image ? [variant.image] : [],
};

export const ViewCollection: React.FC<ViewCollectionPropsType> = ({
  code: codeProp,
  basePath = `${baseHost}/api/v1/collection`,
}) => {
  const code = useMemo(() => codeProp ?? parseCodeFromUrl(), [codeProp]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | undefined>();
  const [variants, setVariants] = useState<VariantType[] | null>(null);

  useEffect(() => {
    let canceled = false;
    const controller = new AbortController();

    const fetchData = async () => {
      if (!code) {
        setError("Код подборки не найден в адресе страницы.");
        return;
      }

      setLoading(true);
      setError(undefined);
      setVariants(null);

      try {
        const response = await axios.get<BackendSuccessType>(
          `${basePath}/${encodeURIComponent(code)}`,
          { signal: controller.signal },
        );
        if (!canceled) setVariants(response?.data?.data?.content?.variants ?? []);
      } catch (requestError: any) {
        if (canceled) return;
        const status = requestError?.response?.status ?? null;
        const data: BackendErrorType | undefined = requestError?.response?.data;
        let message = data?.message || (
          status && status >= 500
            ? `Сервер недоступен (${status}). Попробуйте позже.`
            : "Не удалось загрузить подборку."
        );

        if (data?.errors && typeof data.errors === "object") {
          const details = Object.values(data.errors).flat().map(String).filter(Boolean);
          if (details.length) message += ` — ${details.join(" • ")}`;
        }
        setError(message);
      } finally {
        if (!canceled) setLoading(false);
      }
    };

    fetchData();
    return () => {
      canceled = true;
      controller.abort();
    };
  }, [basePath, code]);

  if (!code) {
    return (
      <main className="collection-page collection-page--message">
        <ErrorBlock title="Некорректный адрес" description="Код подборки не распознан. Проверьте ссылку и попробуйте снова." />
      </main>
    );
  }

  const variantsCount = variants?.length ?? 0;

  return (
    <main className="collection-page">
      <header className="collection-page__header">
        <span className="collection-page__eyebrow">Подборка недвижимости</span>
        <h1>Варианты для вас</h1>
        <p>{loading ? "Загружаем подборку…" : variantsCount ? `В подборке ${variantsCount} ${pluralizeRu(variantsCount, ["вариант", "варианта", "вариантов"])}.` : ""}</p>
      </header>

      {loading && <div className="collection-page__loading" role="status">Загружаем варианты…</div>}
      {error && <ErrorBlock title="Не удалось открыть подборку" description={error} />}

      {!loading && !error && !variantsCount && (
        <div className="collection-page__empty">В этой подборке пока нет вариантов.</div>
      )}

      {!loading && !error && variantsCount > 0 && (
        <ul className="collection-page__list">
          {variants!.map((variant) => {
            const housing = toHousing(variant);
            const plan = installmentLabel(variant.installmentPlan, variant.installmentPeriod);
            const note = typeof variant.monthlyPrice === "number"
              ? `${plan} · от ${formatRub(variant.monthlyPrice)} / мес.`
              : plan;

            return (
              <HousingItem
                key={variant.id}
                housing={housing}
                hideAction
                className="collection-page__card"
                meta={(
                  <>
                    <span className="housing-item_meta-item">
                      {variant.roomType || "Планировка"}
                      {variant.area != null ? ` · ${variant.area} м²` : ""}
                    </span>
                    {variant.floorRange && <span className="housing-item_meta-item">Этаж: {variant.floorRange}</span>}
                  </>
                )}
                price={{
                  label: "Стоимость",
                  value: formatRub(variant.fullPrice),
                  note,
                }}
              />
            );
          })}
        </ul>
      )}
    </main>
  );
};
